/**
 * <ds-brand-carousel> — touch-friendly horizontal scroll carousel.
 * Uses native scroll-snap + IntersectionObserver for dot indicators.
 * Zero external dependencies. Respects prefers-reduced-motion.
 *
 * @element ds-brand-carousel
 * @cssclass .ds-brand-track      — the scrolling container
 * @cssclass .ds-brand-card       — each slide
 * @cssclass .ds-brand-track__dots — dot indicator nav (built by JS)
 */
(() => {
  'use strict';

  if (customElements.get('ds-brand-carousel')) return;

  class DsBrandCarousel extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('.ds-brand-card')];
      this.dotNav = this.querySelector('.ds-brand-track__dots');

      if (this.cards.length < 2) {
        // Hide dots if only one card
        if (this.dotNav) this.dotNav.hidden = true;
        return;
      }

      this._buildDots();
      this._observeSlides();
    }

    /**
     * Build dot indicator buttons — one per brand card.
     * @private
     */
    _buildDots() {
      if (!this.dotNav) return;

      const fragment = document.createDocumentFragment();

      this.cards.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'ds-brand-track__dot' + (i === 0 ? ' is-active' : '');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to brand ${i + 1}`);
        dot.dataset.index = i;
        fragment.appendChild(dot);
      });

      this.dotNav.innerHTML = '';
      this.dotNav.appendChild(fragment);

      this.dotNav.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-index]');
        if (!btn) return;

        const index = parseInt(btn.dataset.index, 10);
        const target = this.cards[index];
        if (!target) return;

        target.scrollIntoView({
          behavior: this._prefersReducedMotion() ? 'auto' : 'smooth',
          inline: 'start',
          block: 'nearest',
        });
      });
    }

    /**
     * Use IntersectionObserver to track which card is most visible and
     * update the active dot indicator accordingly.
     * @private
     */
    _observeSlides() {
      if (!this.dotNav) return;

      const dots = this.dotNav.querySelectorAll('.ds-brand-track__dot');

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const idx = this.cards.indexOf(entry.target);
            if (idx === -1) continue;
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === idx));
          }
        },
        { root: this, threshold: 0.6 }
      );

      this.cards.forEach((card) => observer.observe(card));
    }

    /**
     * Check if the user prefers reduced motion.
     * @private
     * @returns {boolean}
     */
    _prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('ds-brand-carousel', DsBrandCarousel);
})();
